/**
 * Deals Grid - Product grid with mystery box integration and infinite scroll
 */

import { useCallback, useRef, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import { useLanguage } from '../../../../contexts/LanguageContext';
import DealsProductCard from './DealsProductCard';
import CurtainLootbox from './CurtainLootbox';
import DealsCardSkeleton, { MysteryBoxSkeleton } from './DealsCardSkeleton';
import EmptyState from './EmptyState';
import type { DealProduct, GroupedDealProduct, MysteryType, DealCategory } from '../types';
import { groupProductsByName } from '../utils/helpers';

interface Props {
  products: DealProduct[];
  category: DealCategory;
  isLoading: boolean;
  isFetchingNextPage: boolean;
  hasNextPage: boolean;
  onLoadMore: () => void;
  onMysteryUnlock: (product: DealProduct, type: MysteryType) => void;
  onMysteryPurchase: (type: MysteryType) => void;
  onLoginRequired: () => void;
  searchQuery?: string;
}

export default function DealsGrid({
  products,
  category,
  isLoading,
  isFetchingNextPage,
  hasNextPage,
  onLoadMore,
  onMysteryUnlock,
  onMysteryPurchase,
  onLoginRequired,
  searchQuery,
}: Props) {
  const { t } = useLanguage();
  const loadMoreRef = useRef<HTMLDivElement>(null);

  // Separate mystery boxes from regular products
  const mysteryProducts = products.filter((p) => p.is_mystery === true);
  const regularProducts = products.filter((p) => p.is_mystery !== true);

  // Group As-Is products by name for better display
  const shouldGroup = category === 'as_is' && !searchQuery;
  const groupedProducts: GroupedDealProduct[] = shouldGroup
    ? groupProductsByName(regularProducts.filter((p) => p.category === 'as_is'))
    : [];
  const ungroupedProducts = shouldGroup
    ? regularProducts.filter((p) => p.category !== 'as_is')
    : regularProducts;

  // Infinite scroll observer
  const handleObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const [entry] = entries;
      if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) {
        onLoadMore();
      }
    },
    [hasNextPage, isFetchingNextPage, onLoadMore]
  );

  useEffect(() => {
    const observer = new IntersectionObserver(handleObserver, {
      root: null,
      rootMargin: '100px',
      threshold: 0,
    });

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => observer.disconnect();
  }, [handleObserver]);

  // Initial loading state
  if (isLoading && products.length === 0) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Mystery boxes skeleton */}
        {category === 'as_is' && (
          <div className="mb-8">
            <h2 className="text-lg font-bold text-gray-900 mb-4">
              {t('dealsGrid.mysteryBoxes', 'Mystery Boxes')}
            </h2>
            <MysteryBoxSkeleton count={4} />
          </div>
        )}
        <DealsCardSkeleton count={12} />
      </div>
    );
  }

  // Empty state
  if (!isLoading && products.length === 0) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <EmptyState
          type={searchQuery ? 'no-results' : 'no-deals'}
          searchQuery={searchQuery}
        />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Mystery Boxes Section (As-Is only) */}
      {category === 'as_is' && mysteryProducts.length > 0 && (
        <section className="mb-10">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <span className="text-2xl">🔮</span>
              {t('dealsGrid.mysteryBoxes', 'Mystery Boxes')}
              <span className="ml-2 px-2 py-0.5 bg-purple-100 text-purple-700 text-sm rounded-full">
                {mysteryProducts.length}
              </span>
            </h2>
            <p className="text-sm text-gray-500">
              {t('dealsGrid.mysteryHint', 'Unlock with Rumios to see early!')}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {mysteryProducts.map((product, index) => (
              <CurtainLootbox
                key={`${product.product_code}-${product.store_id}-mystery`}
                product={product}
                mysteryType={product.mystery_type || 'standard'}
                onUnlock={onMysteryUnlock}
                onPurchase={onMysteryPurchase}
                onLoginRequired={onLoginRequired}
                index={index}
              />
            ))}
          </div>
        </section>
      )}

      {/* Regular Products Section */}
      {(regularProducts.length > 0 || groupedProducts.length > 0) && (
        <section>
          {/* Section Header */}
          {category === 'as_is' && mysteryProducts.length > 0 && (
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900">
                {t('dealsGrid.availableDeals', 'Available Deals')}
                <span className="ml-2 px-2 py-0.5 bg-gray-100 text-gray-600 text-sm rounded-full">
                  {shouldGroup ? groupedProducts.length : regularProducts.length}
                </span>
              </h2>
            </div>
          )}

          {/* Grouped Products (As-Is) */}
          {shouldGroup && groupedProducts.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {groupedProducts.map((group, index) => (
                <DealsProductCard
                  key={`${group.primary.product_code}-group`}
                  product={group.primary}
                  group={group}
                  index={index}
                />
              ))}
            </div>
          )}

          {/* Ungrouped Products */}
          {ungroupedProducts.length > 0 && (
            <div
              className={`grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 ${
                shouldGroup && groupedProducts.length > 0 ? 'mt-8' : ''
              }`}
            >
              {ungroupedProducts.map((product, index) => (
                <DealsProductCard
                  key={`${product.product_code}-${product.store_id}`}
                  product={product}
                  index={index}
                />
              ))}
            </div>
          )}

          {/* Non-grouped regular products for non-As-Is categories */}
          {!shouldGroup && regularProducts.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {regularProducts.map((product, index) => (
                <DealsProductCard
                  key={`${product.product_code}-${product.store_id}`}
                  product={product}
                  index={index}
                />
              ))}
            </div>
          )}
        </section>
      )}

      {/* Load More Trigger */}
      <div ref={loadMoreRef} className="h-10 mt-8" />

      {/* Loading More Indicator */}
      {isFetchingNextPage && (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-primary-600 mr-2" />
          <span className="text-gray-600">{t('dealsGrid.loadingMore', 'Loading more deals...')}</span>
        </div>
      )}

      {/* End of Results */}
      {!hasNextPage && products.length > 0 && (
        <div className="text-center py-8 text-gray-500">
          <p>{t('dealsGrid.endOfResults', "You've seen all the deals!")}</p>
        </div>
      )}
    </div>
  );
}
